<template>
    <v-container fluid>
      <v-card>
        <v-card-title>Weekly Meal Planner</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="selectedWeek"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="selectedWeek"
                    label="Select Week"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="selectedWeek"
                  type="week"
                  no-title
                  scrollable
                  @input="$refs.menu.save(selectedWeek)"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-alert type="info" outlined>
                Available Points: {{ availablePoints }}
              </v-alert>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn color="primary" @click="saveWeeklyPlan" :disabled="!canSavePlan">
                Save Weekly Plan
              </v-btn>
            </v-col>
          </v-row>
  
          <v-data-table
            :headers="headers"
            :items="weeklyPlan"
            :items-per-page="7"
            class="elevation-1"
          >
            <template v-slot:item.date="{ item }">
              {{ formatDate(item.date) }}
            </template>
            <template v-slot:item.breakfast="{ item }">
              <meal-selector
                :meal="item.breakfast"
                meal-type="Breakfast"
                @update:meal="updateMeal(item, 'breakfast', $event)"
              />
            </template>
            <template v-slot:item.lunch="{ item }">
              <meal-selector
                :meal="item.lunch"
                meal-type="Lunch"
                @update:meal="updateMeal(item, 'lunch', $event)"
              />
            </template>
            <template v-slot:item.dinner="{ item }">
              <meal-selector
                :meal="item.dinner"
                meal-type="Dinner"
                @update:meal="updateMeal(item, 'dinner', $event)"
              />
            </template>
            <template v-slot:item.totalPoints="{ item }">
              {{ calculateDailyPoints(item) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                small
                color="primary"
                @click="openCustomizeDialog(item)"
                :disabled="isEditingDisabled(item)"
              >
                Customize
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
  
      <v-card class="mt-4">
        <v-card-title>Weekly Summary</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">Total Points Used: {{ totalPointsUsed }}</v-col>
            <v-col cols="6">Remaining Points: {{ availablePoints }}</v-col>
          </v-row>
          <v-chip-group>
            <v-chip v-for="(count, mealType) in mealTypeCounts" :key="mealType">
              {{ mealType }}: {{ count }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>
  
      <v-dialog v-model="customizeDialog" max-width="600px">
        <v-card>
          <v-card-title>Customize Meals for {{ formatDate(selectedDate) }}</v-card-title>
          <v-card-text>
            <v-stepper v-model="customizationStep">
              <v-stepper-header>
                <v-stepper-step :complete="customizationStep > 1" step="1">Breakfast</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="customizationStep > 2" step="2">Lunch</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="3">Dinner</v-stepper-step>
              </v-stepper-header>
  
              <v-stepper-items>
                <v-stepper-content step="1">
                  <meal-customizer
                    :meal="customMeals.breakfast"
                    @update:meal="updateCustomMeal('breakfast', $event)"
                  />
                </v-stepper-content>
                <v-stepper-content step="2">
                  <meal-customizer
                    :meal="customMeals.lunch"
                    @update:meal="updateCustomMeal('lunch', $event)"
                  />
                </v-stepper-content>
                <v-stepper-content step="3">
                  <meal-customizer
                    :meal="customMeals.dinner"
                    @update:meal="updateCustomMeal('dinner', $event)"
                  />
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeCustomizeDialog">Cancel</v-btn>
            <v-btn color="blue darken-1" text @click="saveCustomMeals">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
        {{ snackbarText }}
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import MealSelector from './MealSelector.vue';
  import MealCustomizer from './MealCustomizer.vue';
  
  export default {
    components: {
      MealSelector,
      MealCustomizer,
    },
    data: () => ({
      menu: false,
      selectedWeek: null,
      headers: [
        { text: 'Date', value: 'date' },
        { text: 'Breakfast', value: 'breakfast' },
        { text: 'Lunch', value: 'lunch' },
        { text: 'Dinner', value: 'dinner' },
        { text: 'Total Points', value: 'totalPoints' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      weeklyPlan: [],
      totalWeeklyPoints: 70, // 10 points per day for a week
      customizeDialog: false,
      customizationStep: 1,
      selectedDate: null,
      customMeals: {
        breakfast: null,
        lunch: null,
        dinner: null,
      },
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    }),
    computed: {
      availablePoints() {
        return this.totalWeeklyPoints - this.totalPointsUsed;
      },
      totalPointsUsed() {
        return this.weeklyPlan.reduce((total, day) => {
          return total + this.calculateDailyPoints(day);
        }, 0);
      },
      mealTypeCounts() {
        const counts = {};
        this.weeklyPlan.forEach(day => {
          ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
            if (day[mealType] && day[mealType].name) {
              counts[day[mealType].name] = (counts[day[mealType].name] || 0) + 1;
            }
          });
        });
        return counts;
      },
      canSavePlan() {
        return this.weeklyPlan.some(day => 
          day.breakfast.name || day.lunch.name || day.dinner.name
        );
      },
    },
    watch: {
      selectedWeek() {
        this.generateWeeklyPlan();
      },
    },
    methods: {
      generateWeeklyPlan() {
        const startDate = new Date(this.selectedWeek);
        this.weeklyPlan = Array.from({ length: 7 }, (_, i) => {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i);
          return {
            date: currentDate,
            breakfast: { name: '', points: 0 },
            lunch: { name: '', points: 0 },
            dinner: { name: '', points: 0 },
          };
        });
      },
      formatDate(date) {
        return new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      },
      updateMeal(item, mealType, meal) {
        item[mealType] = meal;
      },
      calculateDailyPoints(item) {
        return item.breakfast.points + item.lunch.points + item.dinner.points;
      },
      isEditingDisabled(item) {
        const today = new Date();
        const itemDate = new Date(item.date);
        const fridayBefore = new Date(itemDate);
        fridayBefore.setDate(itemDate.getDate() - ((itemDate.getDay() + 2) % 7));
        return today > fridayBefore;
      },
      openCustomizeDialog(item) {
        this.selectedDate = item.date;
        this.customMeals = {
          breakfast: { ...item.breakfast },
          lunch: { ...item.lunch },
          dinner: { ...item.dinner },
        };
        this.customizeDialog = true;
      },
      closeCustomizeDialog() {
        this.customizeDialog = false;
        this.customizationStep = 1;
      },
      updateCustomMeal(mealType, meal) {
        this.customMeals[mealType] = meal;
      },
      saveCustomMeals() {
        const dayIndex = this.weeklyPlan.findIndex(day => day.date.getTime() === this.selectedDate.getTime());
        if (dayIndex !== -1) {
          this.weeklyPlan[dayIndex].breakfast = { ...this.customMeals.breakfast };
          this.weeklyPlan[dayIndex].lunch = { ...this.customMeals.lunch };
          this.weeklyPlan[dayIndex].dinner = { ...this.customMeals.dinner };
        }
        this.closeCustomizeDialog();
        this.showSnackbar('Meals customized successfully', 'success');
      },
      saveWeeklyPlan() {
        // Here you would typically send this data to your backend
        console.log('Saving weekly plan:', this.weeklyPlan);
        this.showSnackbar('Weekly plan saved successfully', 'success');
      },
      showSnackbar(text, color) {
        this.snackbarText = text;
        this.snackbarColor = color;
        this.snackbar = true;
      },
    },
  };
  </script>